import { type MouseEvent, useCallback, useMemo } from 'react';

import type { PagesProps } from '#pagination/components/types/pages.props';

import { usePaginationSelector } from '#pagination/context/hooks/selector.hook';
import { usePaginationStoreSet } from '#pagination/context/hooks/set.hook';
import { useTotalPages } from '#pagination/hooks/total-pages.hook';

/**
 * Custom hook to configure pagination controls and state for a paginated component.
 *
 * This hook calculates the visible page numbers (with ellipsis for large page sets),
 * determines navigation state (next/previous page availability), and provides
 * event handlers for pagination button clicks.
 *
 * @param params - Pagination configuration options.
 * @param params.maxVisiblePages - Maximum number of visible page buttons (default: 5).
 * @param params.onFirstPage - Callback invoked when the "first page" button is clicked.
 * @param params.onLastPage - Callback invoked when the "last page" button is clicked.
 * @param params.onNextPage - Callback invoked when the "next page" button is clicked.
 * @param params.onPage - Callback invoked when a specific page button is clicked.
 * @param params.onPreviousPage - Callback invoked when the "previous page" button is clicked.
 *
 * @returns An object containing:
 * - `pages`: Array of page numbers and ellipsis ('...') for rendering pagination controls.
 * - `hasNextPage`: Whether there is a next page available.
 * - `hasPreviousPage`: Whether there is a previous page available.
 * - `handleFirstPageClick`: Event handler for the "first page" button.
 * - `handleLastPageClick`: Event handler for the "last page" button.
 * - `handleNextPageClick`: Event handler for the "next page" button.
 * - `handlePageClick`: Event handler for a specific page button.
 * - `handlePreviousPageClick`: Event handler for the "previous page" button.
 * - `lastClicked`: The last clicked page or navigation action.
 * - `page`: The current page number.
 * - `totalPages`: The total number of pages.
 *
 * @remarks
 * - This hook relies on pagination context selectors and setters for state management.
 * - The returned handlers should be attached to the respective pagination UI elements.
 */
export const usePagesConfiguration = ({
  maxVisiblePages = 5,
  onFirstPage,
  onLastPage,
  onNextPage,
  onPage,
  onPreviousPage,
}: Omit<PagesProps, 'isLoading' | 'showFirstLastButtons'>) => {
  const lastClicked = usePaginationSelector(({ lastClickedPage }) => {
    return lastClickedPage;
  });
  const page = usePaginationSelector(({ page }) => {
    return page;
  });
  const set = usePaginationStoreSet();
  const totalPages = useTotalPages();

  const state = useMemo(() => {
    const currentPage = page;
    const maxDynamicPages = maxVisiblePages - 1;
    const pages: ('...' | number)[] = [];

    if (totalPages <= maxDynamicPages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }

      return {
        hasNextPage: currentPage < totalPages,
        hasPreviousPage: currentPage > 1,
        pages,
      };
    }

    const halfWindow = Math.floor(maxDynamicPages / 2);

    let windowStart: number;
    let windowEnd: number;

    windowStart = currentPage - halfWindow;
    windowEnd = currentPage + maxDynamicPages - halfWindow - 1;

    if (currentPage <= halfWindow + 1) {
      windowStart = 2; // Always start the dynamic window at 2
      windowEnd = 1 + maxDynamicPages; // End after N dynamic pages
    } else if (currentPage >= totalPages - halfWindow) {
      windowEnd = totalPages - 1; // End the window right before the last page
      windowStart = windowEnd - maxDynamicPages + 1; // Start N pages back
    }

    windowStart = Math.max(2, windowStart);
    windowEnd = Math.min(totalPages - 1, windowEnd);

    pages.push(1); // Always Page 1

    // if the window starts AFTER page 2, we need an ellipsis
    if (windowStart > 2) {
      pages.push('...');
    }

    for (let i = windowStart; i <= windowEnd; i++) {
      pages.push(i);
    }

    // if the window ends BEFORE the last page - 1, we need an ellipsis
    if (windowEnd < totalPages - 1) {
      pages.push('...');
    }

    if (totalPages > 1 && pages[pages.length - 1] !== totalPages) {
      pages.push(totalPages);
    }

    // remove any accidental duplicate '...' if necessary
    const finalPages = pages.filter((ellipsis, i, arr) => {
      return ellipsis !== '...' || arr[i - 1] !== '...';
    });

    return {
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
      pages: finalPages,
    };
  }, [maxVisiblePages, page, totalPages]);

  const handleFirstPageClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (!state.hasPreviousPage) {
        e.preventDefault();

        return;
      }

      set({ lastClickedPage: 'first' });
      onFirstPage();
    },
    [onFirstPage, set, state.hasPreviousPage]
  );

  const handleLastPageClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (!state.hasNextPage) {
        e.preventDefault();

        return;
      }

      set({ lastClickedPage: 'last' });
      onLastPage();
    },
    [onLastPage, set, state.hasNextPage]
  );

  const handleNextPageClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (!state.hasNextPage) {
        e.preventDefault();

        return;
      }

      set({ lastClickedPage: 'next' });
      onNextPage();
    },
    [onNextPage, set, state.hasNextPage]
  );

  const handlePageClick = useCallback(
    (pageIndex: number) => {
      set({ lastClickedPage: pageIndex });
      onPage(pageIndex);
    },
    [onPage, set]
  );

  const handlePreviousPageClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (!state.hasPreviousPage) {
        e.preventDefault();

        return;
      }

      set({ lastClickedPage: 'previous' });
      onPreviousPage();
    },
    [onPreviousPage, set, state.hasPreviousPage]
  );

  return {
    ...state,
    handleFirstPageClick,
    handleLastPageClick,
    handleNextPageClick,
    handlePageClick,
    handlePreviousPageClick,
    lastClicked,
    page,
    totalPages,
  };
};
